import { createElement, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/utils.js';

export type HeadingLevel = 'display' | 1 | 2 | 3 | 4 | 5 | 6;

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  children: ReactNode;
}

const headingClassName: Record<HeadingLevel, string> = {
  display: 'h-display',
  1: 'h-1',
  2: 'h-2',
  3: 'h-3',
  4: 'h-4',
  5: 'h-5',
  6: 'h-6',
};

const headingTag: Record<HeadingLevel, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  display: 'h1',
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
};

export function Heading({ level = 2, className, children, ...props }: HeadingProps) {
  return createElement(
    headingTag[level],
    { className: cn(headingClassName[level], className), ...props },
    children,
  );
}

export type TextVariant = 'body' | 'small' | 'xs' | 'caps';

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: 'p' | 'span' | 'div';
  variant?: TextVariant;
  mono?: boolean;
  muted?: boolean;
  subtle?: boolean;
  children: ReactNode;
}

const variantClassName: Record<TextVariant, string> = {
  body: 't-body',
  small: 't-small',
  xs: 't-xs',
  caps: 't-caps',
};

export function Text({
  as: Tag = 'span',
  variant = 'body',
  mono = false,
  muted = false,
  subtle = false,
  className,
  children,
  ...props
}: TextProps) {
  return (
    <Tag
      className={cn(
        variantClassName[variant],
        mono && 't-mono',
        muted && 't-muted',
        subtle && 't-subtle',
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}
